import InformationBadge from '@c/InformationBadge';
import { prefix } from '@util/imagePrefix';
import { ProductDocument } from 'models/product';
import { UserDocument } from 'models/user';
import Image from 'next/image';
import { useState } from 'react';
import EditYourGarage from './components/EditYourGarage';
import GarageBikeCard from './components/GarageBikeCard';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';

type ProductFitmentTabProps = {
  product: ProductDocument;
  userDoc?: UserDocument | null;
};

export default function ProductFitmentTab({
  product,
  userDoc,
}: ProductFitmentTabProps) {
  const fittingMakes = new Set(
    product?.fitting_bikes?.bikes?.map((b) => b.make)
  );

  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  if (!product.fitting_bikes?.bikes || !product.fitting_bikes?.bikes.length) {
    return (
      <div className="flex h-[10rem] w-full items-center justify-center px-4 py-16 sm:px-0">
        <InformationBadge
          text="This product has no fitment information. Please verify compatibility with the seller."
          type="info"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-16 px-8 py-8">
      {userDoc && (
        <div className="flex flex-col gap-y-4">
          <EditYourGarage product={product} userDoc={userDoc!} />

          <div className="flex w-full flex-wrap items-center gap-8">
            {userDoc &&
              userDoc.bikes.map((bike) => (
                <GarageBikeCard
                  bike={bike}
                  key={`bike-${bike.model}-${bike.make}-${bike.year}`}
                  product={product}
                />
              ))}
          </div>
        </div>
      )}

      {Array.from(fittingMakes).map((make) => {
        const parsedBrandName = make.split(' ').join('').toLowerCase();
        const brandImage = `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`;

        const compatibleVehicles = product.fitting_bikes?.bikes
          .filter((b) => b.make === make)
          .filter((v) => v.make && v.year && v.model);

        const mergeVehicles = () => {
          const mergedVehicles: Array<{
            make: string;
            model: string;
            years: number[];
          }> = [];

          if (compatibleVehicles) {
            const processedVehicles = new Set<string>();

            for (const vehicle of compatibleVehicles) {
              const vehicleKey = `${vehicle.make}-${vehicle.model}`;

              if (processedVehicles.has(vehicleKey)) continue;
              processedVehicles.add(vehicleKey);

              const otherIdenticalVehicles = compatibleVehicles.filter(
                (v) => v.make === vehicle.make && v.model === vehicle.model
              );

              const years = otherIdenticalVehicles
                .map((v) => v.year)
                .filter((year): year is number => year !== null)
                .sort((a, b) => a - b);

              mergedVehicles.push({
                make: vehicle.make,
                model: vehicle.model ?? '',
                years,
              });
            }
          }

          return mergedVehicles;
        };

        const validVehicles = mergeVehicles();
        const hasOnlyInvalidVehicles = !validVehicles.length;
        const years = product.fitting_bikes?.bikes
          .filter((b) => b.make === make && b.year)
          .map((b) => b.year)
          .sort((a, b) => a! - b!);
        const yearRange = years
          ? years.length > 1
            ? `${years[0]} - ${years[years.length - 1]}`
            : years[0]
          : null;

        return (
          <div key={make} className="flex flex-col gap-y-4">
            <div className="flex items-center gap-x-8 border-b border-zinc-200">
              {failedImages.has(make) ? (
                <div className="flex h-[7rem] w-[7rem] items-center justify-center rounded-[1rem] bg-gray-100 text-center">
                  <span className="text-[1.2rem] font-semibold text-gray-600">
                    {make}
                  </span>
                </div>
              ) : (
                <Image
                  src={brandImage}
                  alt={make}
                  height={300}
                  width={300}
                  className="h-[7rem] w-[7rem] object-contain"
                  onError={() => {
                    setFailedImages((prev) => new Set(prev).add(make));
                  }}
                />
              )}

              {hasOnlyInvalidVehicles ? (
                <div className="flex items-end gap-x-4">
                  <div className="hidden sm:flex">
                    <InformationCircleIcon
                      className="h-10 w-10 text-zinc-700 hover:cursor-help"
                      data-tooltip-id={`fitment-info-${make}`}
                      data-tooltip-content={`This product is compatible with ${make} vehicles, but we don't have detailed model and year information. Please contact the seller to verify exact fitment for your specific bike.`}
                    />
                    <Tooltip
                      id={`fitment-info-${make}`}
                      className="w-[10rem]"
                    />
                  </div>

                  <span
                    className={
                      'text-[1.6rem] font-semibold text-black sm:text-[1.8rem]'
                    }
                  >
                    Compatible with {yearRange ? yearRange : ''} {make} vehicles
                  </span>
                </div>
              ) : (
                <span
                  className={
                    'text-[1.6rem] font-semibold text-black sm:text-[1.8rem]'
                  }
                >
                  Compatible with {compatibleVehicles?.length} {make} vehicle(s)
                </span>
              )}
            </div>

            {hasOnlyInvalidVehicles ? (
              <div className="flex h-[12rem] items-center justify-center">
                <p className="text-[1.6rem] font-medium text-zinc-600 sm:text-[1.8rem]">
                  Contact the seller for exact {yearRange ? '' : 'year and'}{' '}
                  model fitment.
                </p>
              </div>
            ) : (
              <div className="mt-4 grid w-full grid-cols-1 items-center gap-y-4 sm:grid-cols-2">
                {validVehicles?.map((bike) => {
                  return (
                    <div
                      className="flex items-center gap-x-4"
                      key={`bike-${Math.min(...bike.years)}-${bike.model}-${
                        bike.make
                      }`}
                    >
                      <span className="text-[1.8rem] font-semibold text-brand-secondary">
                        {bike.model}
                      </span>

                      {bike.years.length > 1 ? (
                        <span className="text-[1.8rem] font-medium text-zinc-700">
                          {Math.min(...bike.years)} - {Math.max(...bike.years)}
                        </span>
                      ) : (
                        <span className="text-[1.8rem] font-medium text-zinc-700">
                          {bike.years[0]}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
